import { forwardRef, useContext, useState } from 'react';
import { UnitsContext } from '../../../../Contexts/UnitsContext';
import { initialRawUnitConfig } from '../../../../utilities/initialConfigs';
import { Modalv2Context } from '../../../../Contexts/Modalv2Context';
import { flattenObject } from '../../../../utilities/helpers';
import { v4 as uuid } from 'uuid';
import { toast } from 'react-toastify';
import { UNIT_CREATION_MODES, CONFIG_KEY_CONSTANTS } from './constants.js';
import { IconButton } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { Button, Grid, Box } from '@mui/material';
import { CONFIG_KEYDATA_CONSTANTS } from '../../../../core/constants';

//mui imports====

import useCommonStyles from '../../../../commonStyles';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';

const {
	ADUNITS_DESKTOP_DEFAULT_UNITS_KEY,
	ADUNITS_DESKTOP_BACKFILL_UNITS_KEY,
	ADUNITS_DESKTOP_NO_MCM_UNITS_KEY,
	ADUNITS_MOBILE_DEFAULT_UNITS_KEY,
	ADUNITS_MOBILE_BACKFILL_UNITS_KEY,
	ADUNITS_MOBILE_NO_MCM_UNITS_KEY,
	ADUNITS_TABLET_DEFAULT_UNITS_KEY,
	ADUNITS_TABLET_BACKFILL_UNITS_KEY,
	ADUNITS_TABLET_NO_MCM_UNITS_KEY
} = CONFIG_KEY_CONSTANTS;

const { CUSTOM_TARGETING_KEYDATA, DEFAULT_PV } = CONFIG_KEYDATA_CONSTANTS;

const CreateUnitModalContent = forwardRef(() => {
	const commonStyleClasses = useCommonStyles();
	const { units, updateUnits, nonArchivedUnitsArray, updateNonArchivedUnitsArray } =
		useContext(UnitsContext);
	const [id, setId] = useState('');
	const [name, setName] = useState('');
	const [createUnitMode, setCreateUnitMode] = useState(UNIT_CREATION_MODES.CREATE_NEW); // two modes CREATE_NEW , COPY_EXISTING
	const [selectedExistingUnit, setSelectedExistingUnit] = useState('');
	const { updateModalContent } = useContext(Modalv2Context);

	const customTargetingKvpKeyData = CUSTOM_TARGETING_KEYDATA + '.targeting';
	const customPageTargetingKvpKeyData = CUSTOM_TARGETING_KEYDATA + '.pageviewTargeting';
	const customTargetingEnabledKeyData = customPageTargetingKvpKeyData + '.enabled';
	const customPageTargetingSecPvKeyData = customPageTargetingKvpKeyData + '.sec_pv_targeting';


	const createNewSectionWithDefaultValues = () => {
		const sectionRef = uuid().toString();

		updateUnits({
			...units,
			[sectionRef]: {
				...initialRawUnitConfig,
				videoSectionId: id,
				sectionRef: sectionRef,
				name: name
			}
		});

		updateNonArchivedUnitsArray([...nonArchivedUnitsArray, sectionRef]);
		updateModalContent(null);
	};

	const copyExistingSection = () => {
		if (!selectedExistingUnit) {
			toast.error('Please select an existing unit', { position: toast.POSITION.BOTTOM_RIGHT });
			return;
		}
		if (!units[selectedExistingUnit]) {
			toast.error('Something went wrong selecting the unit', {
				position: toast.POSITION.BOTTOM_RIGHT
			});
			return;
		}
		const flattenedNewExistingSection = flattenObject(units[selectedExistingUnit]);
		const sectionRef = uuid().toString();

		const updatedSection = { ...flattenedNewExistingSection };

		delete updatedSection[customTargetingKvpKeyData]?.ab_split_test;
		updatedSection[customPageTargetingSecPvKeyData] = DEFAULT_PV;

		delete updatedSection.abSplitTestData;

		updateUnits({
			...units,
			[sectionRef]: {
				...updatedSection,
				videoSectionId: id,
				sectionRef: sectionRef,
				name: name,
				[customTargetingEnabledKeyData]: true,
				// Resetting the adUnits to empty arrays, since we don't want to copy the adUnits from the existing section
				[ADUNITS_DESKTOP_DEFAULT_UNITS_KEY]: [],
				[ADUNITS_DESKTOP_BACKFILL_UNITS_KEY]: [],
				[ADUNITS_DESKTOP_NO_MCM_UNITS_KEY]: [],
				[ADUNITS_MOBILE_DEFAULT_UNITS_KEY]: [],
				[ADUNITS_MOBILE_BACKFILL_UNITS_KEY]: [],
				[ADUNITS_MOBILE_NO_MCM_UNITS_KEY]: [],
				[ADUNITS_TABLET_DEFAULT_UNITS_KEY]: [],
				[ADUNITS_TABLET_BACKFILL_UNITS_KEY]: [],
				[ADUNITS_TABLET_NO_MCM_UNITS_KEY]: []
			}
		});
		updateNonArchivedUnitsArray([...nonArchivedUnitsArray, sectionRef]);
		updateModalContent(null);
	};

	const createVideoUnit = () => {
		if (!id || !name) {
			toast.error('Id or name missing', { position: toast.POSITION.BOTTOM_RIGHT });
			return;
		}
		if (units[id]) {
			toast.error('Video Section Unit with this id already exists!!', {
				position: toast.POSITION.BOTTOM_RIGHT
			});
			return;
		}
		if (createUnitMode === UNIT_CREATION_MODES.CREATE_NEW) createNewSectionWithDefaultValues();
		else copyExistingSection();
	};

	const SettingsButton = () => (
		<IconButton
			onClick={() => {
				const uuidString = uuid().toString();
				setId(uuidString);
				const newName = `AP_INSTREAM_${uuidString.substring(0, 5)}`;
				setName(newName);
			}}
		>
			<SettingsIcon />
		</IconButton>
	);

	return (
		<Grid
			container
			spacing={2}
			className={commonStyleClasses.modalContentStyle}
			height="fit-content"
			width="fit-content !important"
		>
			<Grid item xs={6} className={commonStyleClasses.modalContentItem}>
				<Box>
					<Typography className={commonStyleClasses.heading} fontWeight={600} variant="h6">
						Video Section Id
					</Typography>
					<TextField
						value={id}
						onChange={(e) => setId(e.target.value)}
						InputProps={{ endAdornment: <SettingsButton /> }}
					/>
				</Box>
			</Grid>
			<Grid item xs={6} className={commonStyleClasses.modalContentItem}>
				<Box>
					<Typography className={commonStyleClasses.heading} fontWeight={600} variant="h6">
						Video Section Name
					</Typography>
					<TextField type="text" value={name} onChange={(e) => setName(e.target.value)} />
				</Box>
			</Grid>

			<TabContext value={createUnitMode}>
				{units && Object.keys(units).length > 0 && (
					<Grid item xs={12} className={commonStyleClasses.modalContentItem}>
						<Box
							sx={{
								borderBottom: 1,
								borderColor: 'divider',
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								marginTop: '3rem'
							}}
						>
							<TabList
								onChange={(e, newVal) => {
									setCreateUnitMode(newVal);
								}}
							>
								<Tab label="Create With Default Values" value={UNIT_CREATION_MODES.CREATE_NEW} />
								<Tab label="Copy Existing Section" value={UNIT_CREATION_MODES.COPY_EXISTING} />
							</TabList>
						</Box>
					</Grid>
				)}

				<Grid item xs={12} className={commonStyleClasses.modalContentItem}>
					<TabPanel value={UNIT_CREATION_MODES.CREATE_NEW}>
						<Button
							variant="contained"
							style={{ width: '10rem', height: '5rem' }}
							onClick={createVideoUnit}
						>
							Create
						</Button>
					</TabPanel>
					<TabPanel value={UNIT_CREATION_MODES.COPY_EXISTING}>
						<FormControl>
							<Select
								value={selectedExistingUnit}
								onChange={(e) => {
									setSelectedExistingUnit(e.target.value);
								}}
								variant="outlined"
								style={{ width: '50rem' }}
							>
								{units &&
									Object.keys(units).length > 0 &&
									Object.keys(units).map((sectionRef) => {
										return (
											<MenuItem value={sectionRef} key={sectionRef}>
												{units[sectionRef].name} ({units[sectionRef].videoSectionId})
											</MenuItem>
										);
									})}
							</Select>
						</FormControl>
						<Button
							variant="contained"
							style={{ width: '10rem', height: '5rem', marginLeft: '1rem' }}
							onClick={createVideoUnit}
						>
							Create
						</Button>
					</TabPanel>
				</Grid>
			</TabContext>
		</Grid>
	);
});

export default CreateUnitModalContent;
